<script setup lang="ts">
import LocationFieldInput from "./LocationFieldInput.vue";
import type { Entity } from "./adapter";
import groupSuggestions, { isValidSuggestions } from "./adapter";
import { computed, ref, watch } from "vue";
import { getEnvVar } from "../../utils/env";
import useSPAFetch from "../../composables/useSPAFetch";
import { useTimeoutFn, watchOnce } from "@vueuse/core";
import { useOriginAirportQuery } from "./useOriginAirportQuery";
import type { LocationData } from "@/../types/form-data";
import { Locales } from "../../../types/tabs";
import type { PlayerName } from "@/../types/common";
import airportMontreal from "./airportMontreal";
import airportToronto from "./airportToronto";

export interface Props {
  playerName?: PlayerName;
  fieldName?: "from" | "to";
  labelName: string;
  placeholderName?: string;
  tooltipMessage?: string;
  journeyType?: string;
  locale?: string;
  origin?: boolean;
  geolocateAirport: boolean;
  latitude?: number;
  longitude?: number;
  prefillLocation?: Entity | null;
  tabId: string;
}

const props = withDefaults(defineProps<Props>(), {
  playerName: "Package",
  fieldName: "from",
  journeyType: "origin",
  locale: Locales.EN,
});

const emit = defineEmits<(e: "selectItem", location: LocationData) => void>();

const url = ref("");

const selectedItem = ref<Entity | null>(props.prefillLocation ?? null);
const suggestions = ref();

const noResult = ref(false);
const apiError = ref(false);
const INPUT_MINIMUM_CHAR = 3;

function searchUrl(search = "") {
  const queryParameters: Record<string, string> = {
    query: search,
    locale: props.locale,
    playerName: props.playerName,
  };
  if (props.origin) {
    queryParameters.journeyType = props.journeyType;
  }
  const queryString = new URLSearchParams(queryParameters);
  return `${getEnvVar("VITE_AUTOSUGGEST_URL")}?${queryString}`;
}

const requestAirport = ref(props.origin && props.geolocateAirport);
const defaultSelect = ref<Entity | null>(null);
const originAirport = ref<Entity | null>(null);

const {
  onFetchResponse,
  isFetching,
  onFetchError,
  execute: executeFetch,
  canAbort,
  abort,
} = useSPAFetch(url, { immediate: false }, () => {
  // token failure during suggestions lookup
  suggestionsNoResult();
});

const { isPending, start, stop } = useTimeoutFn(
  () => {
    executeFetch();
  },
  getEnvVar("VITE_AUTOSUGGEST_REQUEST_DELAY"),
  {
    immediate: false,
  }
);

onFetchResponse(async (response) => {
  const apiResponse = await response.json();

  if (apiResponse.suggestions.length && isValidSuggestions(apiResponse)) {
    suggestions.value = groupSuggestions(apiResponse);
  } else {
    suggestionsNoResult();
  }
});

function suggestionsNoResult() {
  noResult.value = true;
}

onFetchError(async (response) => {
  if (response.message !== "Not Found") {
    apiError.value = true;
  }
});

if (requestAirport.value) {
  const { originAirportQuery } = useOriginAirportQuery(props.locale);

  const originAirportUrl = computed(() => {
    return originAirportQuery.value
      ? `${getEnvVar("VITE_GEOLOC_URL")}?${originAirportQuery.value}`
      : null;
  });

  const fetchAirport = (airportUrl: string) => {
    const { onFetchResponse } = useSPAFetch(airportUrl, {}, () => {
      // if the token fails we will auto-fill Toronto or Montreal filled in as the departure airport
      if (props.locale === Locales.FR) {
        updateOrigin(airportMontreal);
      } else {
        updateOrigin(airportToronto);
      }
    });
    onFetchResponse(async (response) => {
      const entityResponse = await response.json();
      if (entityResponse.destinationId) {
        updateOrigin(entityResponse);
      }
    });
  };

  watchOnce(originAirportUrl, (url) => {
    if (url) {
      fetchAirport(url);
    }
  });
}

const cancelRequest = () => {
  if (isPending.value) {
    stop();
  }
  if (isFetching.value && canAbort.value) {
    abort();
  }
};

const onType = (input: string) => {
  requestAirport.value = false;
  reset();
  if (input.length >= INPUT_MINIMUM_CHAR) {
    url.value = searchUrl(input);
    suggestions.value = null;
    start();
  }
};

// select the origin airport only when the user has not initiated a manual search
const updateOrigin = (item: Entity) => {
  originAirport.value = item;
  if (!isFetching.value) {
    updateSelection(item);
  }
};

const inputKey = ref("");
const updateSelection = (item: Entity | null) => {
  if (item) {
    defaultSelect.value = item;
    inputKey.value = item.destinationId + Date.now();
  }
  selectedItem.value = item;
};

const reset = (hardReset?: boolean) => {
  cancelRequest();
  suggestions.value = null;
  apiError.value = false;
  noResult.value = false;
  url.value = "";
  if (hardReset) {
    inputKey.value = "" + Date.now();
  }
};

const onBlur = () => {
  let resetField = true;
  if (defaultSelect.value) {
    resetField = false;
    updateSelection(defaultSelect.value);
  }
  reset(resetField);
};

const onSelect = (item: Entity) => {
  updateSelection(item);
  reset();
};

watch(selectedItem, (item) => {
  if (item) {
    emit("selectItem", item);
  }
});

const pendingRequest = computed(() => {
  return isPending.value || isFetching.value;
});
</script>

<template>
  <location-field-input
    :loading="pendingRequest"
    :suggestions="suggestions"
    :no-result="noResult"
    :error="apiError"
    :key="inputKey"
    :default-item="selectedItem"
    :input-name="fieldName"
    :label="labelName"
    :placeholder="placeholderName"
    :tooltipMessage="tooltipMessage"
    :is-origin="origin"
    :tabId="tabId"
    @type="onType"
    @select="onSelect"
    @onBlur="onBlur"
  />
</template>
